import { useInfiniteQuery } from 'react-query'
import axios from 'utils/axios'
import { ShippingMethodsPageDto } from '@merchx-v2/shared-types'
import { guardFromErrors, extractErrorInfo } from 'utils/graphqlHelpers'

const DEFAULT_PAGE_SIZE = 30

/// ////////////////////////////////////////////////////////////////////////////////
// TYPES
/// ////////////////////////////////////////////////////////////////////////////////

export type ShippingMethodsPage = ShippingMethodsPageDto & {
  currentPage: number
  pageSize: number
}

/// ////////////////////////////////////////////////////////////////////////////////
// GRAPHQL
/// ////////////////////////////////////////////////////////////////////////////////

type QueryResponse = GraphQLResponse<'shippingMethodsListPage', ShippingMethodsPage>

const FETCH_SHIPPING_METHODS_LIST = `
  query fetchShippingMethodsList($page: Int, $size: Int) {
    shippingMethodsListPage(page: $page, size: $size) {
      items {
        id
        carrierCode
        serviceCode
        isDefault
        createdAt
        updatedAt
      }
      total
    }
  }
`

/// ////////////////////////////////////////////////////////////////////////////////
// FUNCTION
/// ////////////////////////////////////////////////////////////////////////////////

type PageParam = {
  page?: number
  size?: number
}

type FetchShippingMethodsListArgs = {
  queryKey: string | string[]
  pageParam?: PageParam
}

type FetchShippingMethodsList = (args: FetchShippingMethodsListArgs) => Promise<ShippingMethodsPage>

const fetchShippingMethodsList: FetchShippingMethodsList = async ({ pageParam }) => {
  const { page = 1, size = DEFAULT_PAGE_SIZE } = pageParam || {}
  try {
    const {
      data: { data, errors }
    }: QueryResponse = await axios.post('/graphql', {
      query: FETCH_SHIPPING_METHODS_LIST,
      variables: { page, size }
    })

    guardFromErrors(errors)

    if (!data) {
      throw new Error('Response body is empty!')
    }

    const { shippingMethodsListPage } = data
    if (!shippingMethodsListPage) {
      throw new Error("Can't get shipping methods list page!")
    }

    return {
      items: shippingMethodsListPage.items,
      total: shippingMethodsListPage.total,
      currentPage: page,
      pageSize: size
    }
  } catch (error) {
    throw new Error(extractErrorInfo(error))
  }
}

/// ////////////////////////////////////////////////////////////////////////////////
// HOOK
/// ////////////////////////////////////////////////////////////////////////////////

type GetNextPageParam = (args: ShippingMethodsPage) => PageParam | undefined

type ShippingMethodsListEvents = {
  keepPreviousData: boolean
  getPreviousPageParam: GetNextPageParam
  getNextPageParam: GetNextPageParam
}

export const shippingMethodsListEvents: ShippingMethodsListEvents = {
  keepPreviousData: true,
  getPreviousPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0 }) => {
    if (currentPage > 1) {
      return {
        page: currentPage - 1,
        size: pageSize
      }
    }

    return undefined
  },
  getNextPageParam: ({ pageSize = DEFAULT_PAGE_SIZE, currentPage = 0, total = 0 }) => {
    if (pageSize * currentPage < total) {
      return {
        page: currentPage + 1,
        size: pageSize
      }
    }

    return undefined
  }
}

export const useShippingMethodsList = () => {
  const cacheKey = ['shippingMethodsList']

  return useInfiniteQuery(
    cacheKey,
    prev =>
      fetchShippingMethodsList({
        queryKey: cacheKey,
        pageParam: { ...prev.pageParam }
      }),
    shippingMethodsListEvents
  )
}
